import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { getDayRoster } from "../../lib/api";
import { todayStr } from "../../lib/date";

function fmtDate(iso) {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

export default function SignupRostersTab() {
  const today = todayStr();
  const [date, setDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getDayRoster(date);
      setRows(res.data.rows);
    } catch {
      toast.error("Failed to load roster");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const handlePrint = () => {
    const dateLabel = fmtDate(date);
    const sectionHtml = (title, list, withResponse = false) => `
      <h3 style="margin-top:18px;margin-bottom:6px">${title}</h3>
      ${list.length === 0 ? '<p style="color:#999">No sign-ups</p>' : list.map(o => `
        <div style="margin-bottom:12px;page-break-inside:avoid">
          <div style="font-weight:bold;font-size:14px">${o.title}${o.slot_name ? ` <span style="color:#666;font-weight:normal">— ${o.slot_name}</span>` : ""}</div>
          <div style="color:#666;font-size:12px;margin-bottom:4px">${[o.lead_staff_name && `Lead: ${o.lead_staff_name}`, o.location, o.capacity != null && `Capacity ${o.capacity}`, o.type === "trip" ? ((o.covered_blocks && o.covered_blocks.length) ? `Blocks: ${o.covered_blocks.map(k => { const m = (k || "").match(/(\d+)/); return m ? `Block ${m[1]}` : k; }).join(", ")}` : "All blocks") : ""].filter(Boolean).join(" · ")}</div>
          ${o.roster.length === 0 ? '<div style="color:#999;font-size:12px;font-style:italic">No students yet</div>' : `<ol style="margin:4px 0 0 18px;padding:0">${o.roster.map(r => `<li style="margin-bottom:2px">${r.student_name}${withResponse ? ` <span style="color:${r.response === "yes" ? "#15803d" : r.response === "no" ? "#b91c1c" : "#a16207"};font-weight:bold">(${(r.response || "").toUpperCase()})</span>` : ""}${r.over_capacity_at_pick ? ' <span style="color:#b91c1c;font-size:10px">⚠ over capacity</span>' : ""}</li>`).join("")}</ol>`}
        </div>`).join("")}
    `;
    const blocks = rows.filter(r => r.type === "block");
    const trips = rows.filter(r => r.type === "trip");
    const w = window.open("", "_blank", "width=900,height=700");
    w.document.write(`<html><head><title>Sign-Up Roster ${date}</title></head><body style="font-family:Arial;padding:24px">
      <h1 style="margin:0">Sign-Up Roster</h1>
      <p style="color:#555;margin-top:4px">${dateLabel}</p>
      ${sectionHtml("Block Activities", blocks)}
      ${sectionHtml("Trips & Events", trips, true)}
      <script>window.onload=()=>window.print()</script>
    </body></html>`);
    w.document.close();
  };

  const blocks = rows.filter(r => r.type === "block");
  const trips = rows.filter(r => r.type === "trip");
  const totalStudents = rows.reduce((acc, r) => acc + (r.roster || []).length, 0);

  return (
    <div className="space-y-4" data-testid="signup-rosters-tab">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <CardTitle>Sign-Up Roster — printable</CardTitle>
              <CardDescription>Who picked what for a given day. Print to PDF or paper.</CardDescription>
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="roster-date" />
              </div>
              <Button onClick={handlePrint} disabled={rows.length === 0 || loading} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="roster-print">
                Print
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#00305E]" /></div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No sign-ups for {fmtDate(date)}.</p>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline">{blocks.length} block activit{blocks.length === 1 ? "y" : "ies"}</Badge>
                <Badge variant="outline">{trips.length} trip{trips.length === 1 ? "" : "s"}</Badge>
                <Badge variant="outline">{totalStudents} total student pick{totalStudents === 1 ? "" : "s"}</Badge>
              </div>
              {blocks.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Block Activities</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {blocks.map((o) => (
                      <div key={o.id} className="border rounded p-3 bg-indigo-50/30">
                        <div className="text-sm font-semibold">{o.title}{o.slot_name && <span className="text-xs text-muted-foreground font-normal"> — {o.slot_name}</span>}</div>
                        <div className="text-xs text-muted-foreground mb-2">{[o.lead_staff_name && `Lead: ${o.lead_staff_name}`, o.location, o.capacity != null && `Cap ${o.capacity}`].filter(Boolean).join(" · ")}</div>
                        {o.roster.length === 0 ? <div className="text-xs italic text-gray-400">No students</div> : (
                          <ol className="text-sm pl-5 list-decimal space-y-0.5">
                            {o.roster.map((r) => <li key={r.id}>{r.student_name}</li>)}
                          </ol>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {trips.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Trips & Events</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {trips.map((o) => (
                      <div key={o.id} className="border rounded p-3 bg-rose-50/30">
                        <div className="text-sm font-semibold">{o.title}</div>
                        <div className="text-xs text-muted-foreground mb-2">{[o.lead_staff_name && `Lead: ${o.lead_staff_name}`, o.location, o.capacity != null && `Cap ${o.capacity}`, (o.covered_blocks && o.covered_blocks.length) ? `Blocks: ${o.covered_blocks.map((k) => { const m = (k || "").match(/(\d+)/); return m ? `Block ${m[1]}` : k; }).join(", ")}` : "All blocks"].filter(Boolean).join(" · ")}</div>
                        {o.roster.length === 0 ? <div className="text-xs italic text-gray-400">No responses</div> : (
                          <ol className="text-sm pl-5 list-decimal space-y-0.5">
                            {o.roster.map((r) => (
                              <li key={r.id}>
                                {r.student_name}{" "}
                                <span className={r.response === "yes" ? "text-green-700 text-xs font-bold" : r.response === "no" ? "text-red-700 text-xs font-bold" : "text-amber-700 text-xs font-bold"}>
                                  ({(r.response || "").toUpperCase()})
                                </span>
                              </li>
                            ))}
                          </ol>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
